/**
 * Lock state, auto-lock, and quick exit. FR-111..FR-113, S-02, S-03.
 *
 * ## Why this is the most important context in the app
 *
 * A1 — an intimate partner or family member with access to the device — is the
 * most FREQUENT adversary in the threat model, well ahead of law enforcement.
 * The features here are the ones that address it, and they are the reason this
 * app is different from a notes file.
 *
 * ## Auto-lock is on both idle and backgrounding
 *
 * Idle alone is not enough: the realistic scenario is the phone being handed
 * over or picked up, not left sitting. `visibilitychange` covers switching apps
 * and locking the screen, which is when it matters most.
 *
 * ## There is no PIN recovery
 *
 * The PIN wraps the data key (ADR-0006). A recovery path would mean the key was
 * recoverable without the PIN, which would mean the encryption was decorative.
 * The UI must say this BEFORE the PIN is set — see `PinSetup`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  initKeyring,
  lock as lockKeyring,
  removePin as removePinFromKeyring,
  setPin as setKeyringPin,
  unlock as unlockKeyring,
  type KeyringState,
} from '@shared/data/private'

export type LockPhase = 'loading' | KeyringState

/** Default 60 seconds. Configurable, including "immediately". FR-112. */
export const DEFAULT_IDLE_MS = 60_000

export interface LockContextValue {
  phase: LockPhase
  /** True while the neutral screen is showing after a quick exit. */
  hidden: boolean
  lastAttemptFailed: boolean
  unlock: (pin: string) => Promise<boolean>
  lockNow: () => void
  setPin: (pin: string) => Promise<void>
  removePin: (pin: string) => Promise<boolean>
  /** FR-113. Clears the view immediately, from anywhere. */
  quickExit: () => void
  reveal: () => void
}

const LockContext = createContext<LockContextValue | undefined>(undefined)

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const

export function LockProvider({
  children,
  idleMs = DEFAULT_IDLE_MS,
}: {
  children: ReactNode
  idleMs?: number
}) {
  const [phase, setPhase] = useState<LockPhase>('loading')
  const [hidden, setHidden] = useState(false)
  const [lastAttemptFailed, setLastAttemptFailed] = useState(false)
  const idleTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    // No lint suppression needed here: the setState calls are inside a `.then`,
    // so the rule's static analysis does not see a synchronous cascade.
    void initKeyring()
      .then(setPhase)
      .catch(() => setPhase('unprotected'))
  }, [])

  const lockNow = useCallback(() => {
    setPhase((current) => {
      if (current === 'unlocked') {
        lockKeyring()
        return 'locked'
      }
      return current
    })
  }, [])

  /**
   * Quick exit. FR-113.
   *
   * Locks first when a PIN exists, so that dismissing the neutral screen cannot
   * reveal anything — then shows the neutral view. Also retitles the document,
   * because a tab title is exactly the sort of thing someone glances at.
   */
  const quickExit = useCallback(() => {
    lockNow()
    setHidden(true)
    document.title = 'Notes'
    // Replace rather than push, so Back does not return to health data.
    if (typeof history !== 'undefined') {
      history.replaceState(null, '', '/')
    }
  }, [lockNow])

  const reveal = useCallback(() => {
    setHidden(false)
    document.title = 'Thana'
  }, [])

  const unlock = useCallback(async (pin: string): Promise<boolean> => {
    try {
      await unlockKeyring(pin)
      setPhase('unlocked')
      setLastAttemptFailed(false)
      return true
    } catch {
      // Deliberately no distinction between "wrong PIN" and any other failure in
      // what the user is told: a message that confirmed the PIN was merely wrong
      // would be a slightly better oracle for someone guessing.
      setLastAttemptFailed(true)
      return false
    }
  }, [])

  const setPin = useCallback(async (pin: string) => {
    await setKeyringPin(pin)
    setPhase('unlocked')
  }, [])

  const removePin = useCallback(async (pin: string): Promise<boolean> => {
    try {
      await removePinFromKeyring(pin)
      setPhase('unprotected')
      return true
    } catch {
      setLastAttemptFailed(true)
      return false
    }
  }, [])

  // Idle auto-lock. Only armed when there is something to lock.
  useEffect(() => {
    if (phase !== 'unlocked') return

    const arm = () => {
      if (idleTimer.current !== undefined) clearTimeout(idleTimer.current)
      idleTimer.current = setTimeout(lockNow, idleMs)
    }

    arm()
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, arm, { passive: true })
    }

    return () => {
      if (idleTimer.current !== undefined) clearTimeout(idleTimer.current)
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, arm)
    }
  }, [phase, idleMs, lockNow])

  // Lock on backgrounding. The realistic case is the phone being handed over or
  // picked up, which idle time alone never notices.
  useEffect(() => {
    if (phase !== 'unlocked') return

    const onHidden = () => {
      if (document.visibilityState === 'hidden') lockNow()
    }
    document.addEventListener('visibilitychange', onHidden)
    return () => document.removeEventListener('visibilitychange', onHidden)
  }, [phase, lockNow])

  // Escape twice in quick succession is the desktop quick exit.
  useEffect(() => {
    let lastEscape = 0
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const now = performance.now()
      if (now - lastEscape < 600) {
        quickExit()
        lastEscape = 0
      } else {
        lastEscape = now
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [quickExit])

  const value = useMemo<LockContextValue>(
    () => ({
      phase,
      hidden,
      lastAttemptFailed,
      unlock,
      lockNow,
      setPin,
      removePin,
      quickExit,
      reveal,
    }),
    [phase, hidden, lastAttemptFailed, unlock, lockNow, setPin, removePin, quickExit, reveal],
  )

  return <LockContext.Provider value={value}>{children}</LockContext.Provider>
}

export function useLock(): LockContextValue {
  const value = useContext(LockContext)
  if (value === undefined) throw new Error('useLock used outside LockProvider')
  return value
}
